import { motion } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { Award, Trophy, Star, Shield, Zap, Target, Cpu, Globe } from 'lucide-react';

const badges = [
    {
        title: "Autonomous Pioneer",
        description: "Successfully deployed 5+ autonomous agent clusters across distributed infrastructure.",
        icon: <Cpu className="w-8 h-8" />,
        color: "from-blue-500 to-cyan-500",
        rarity: "LEGENDARY",
        date: "2025.10"
    },
    {
        title: "Hackathon Architect",
        description: "1st Place Winner at Blockchain+AI Global Summit for NeuraDNS protocol.",
        icon: <Trophy className="w-8 h-8" />,
        color: "from-amber-400 to-orange-600",
        rarity: "EPIC",
        date: "2025.02"
    },
    {
        title: "Full Stack Commander",
        description: "Engineered high-performance hybrid infrastructure with 99.99% Neural Sync uptime.",
        icon: <Zap className="w-8 h-8" />,
        color: "from-emerald-400 to-teal-600",
        rarity: "RARE",
        date: "2024.12"
    },
    {
        title: "Security Sentinel",
        description: "Hardened CyborgDB vector vault with multi-layer cold-wallet encryption patterns.",
        icon: <Shield className="w-8 h-8" />,
        color: "from-purple-500 to-indigo-600",
        rarity: "RARE",
        date: "2025.01"
    },
    {
        title: "Data Alchemist",
        description: "Processed 500M+ data points via EcoScraper distributed hierarchy.",
        icon: <Target className="w-8 h-8" />,
        color: "from-rose-500 to-pink-600",
        rarity: "UNCOMMON",
        date: "2024.08"
    },
    {
        title: "Global Sync Node",
        description: "Contributed to 10+ open-source architectural patterns for Agentic workflow orchestration.",
        icon: <Globe className="w-8 h-8" />,
        color: "from-sky-400 to-blue-600",
        rarity: "UNCOMMON",
        date: "2023.11"
    }
];

export function Achievements() {
    return (
        <div className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-24"
            >
                <div className="inline-flex items-center justify-center p-3 bg-emerald-500/10 rounded-2xl mb-6 border border-emerald-500/20">
                    <Award className="text-emerald-500 w-8 h-8" />
                </div>
                <h1 className="text-5xl md:text-8xl font-black text-white tracking-tighter mb-6">
                    SERVICE <span className="text-emerald-500">HONORS</span>
                </h1>
                <p className="text-gray-400 font-mono tracking-widest uppercase text-sm md:text-base">
                    Verified cryptographic proofs of technical excellence.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {badges.map((badge, idx) => (
                    <motion.div
                        key={badge.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                    >
                        <GlassCard className="p-1 group hover:border-emerald-500/50 transition-all duration-500 overflow-hidden relative border-white/5 h-full">
                            <div className="bg-black/40 p-8 h-full flex flex-col">
                                {/* Glow Background */}
                                <div className={`absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br ${badge.color} opacity-0 group-hover:opacity-20 blur-[60px] transition-opacity duration-700`} />

                                <div className="flex justify-between items-start mb-12">
                                    <div className={`p-4 bg-gradient-to-br ${badge.color} rounded-2xl shadow-lg border border-white/20 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                                        <div className="text-white">
                                            {badge.icon}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-1">{badge.rarity}</div>
                                        <div className="text-xs font-mono text-emerald-500/50">{badge.date}</div>
                                    </div>
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors uppercase tracking-tight">
                                        {badge.title}
                                    </h3>
                                    <p className="text-sm text-gray-400 leading-relaxed font-mono opacity-80 group-hover:opacity-100 transition-opacity">
                                        {badge.description}
                                    </p>
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/5 flex justify-between items-center">
                                    <div className="flex gap-1">
                                        {[1, 2, 3].map(i => (
                                            <Star key={i} size={10} className={i <= (idx < 2 ? 3 : 2) ? "text-emerald-500 fill-emerald-500" : "text-gray-800"} />
                                        ))}
                                    </div>
                                    <span className="text-[8px] font-mono text-gray-600 tracking-[0.3em] uppercase">Auth_Verified</span>
                                </div>
                            </div>
                        </GlassCard>
                    </motion.div>
                ))}
            </div>

            {/* Hall of Fame Note */}
            <div className="mt-32 p-12 rounded-3xl border border-white/5 bg-gradient-to-b from-white/5 to-transparent text-center">
                <Trophy className="text-emerald-500/20 w-16 h-16 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-white mb-4">CONTINUOUS EVOLUTION</h3>
                <p className="text-gray-500 text-sm font-mono max-w-xl mx-auto uppercase tracking-widest leading-loose">
                    The technical landscape changes every 4s. My badge collection is a living record of adaptation and architectural mastery.
                </p>
            </div>
        </div>
    );
}
